// src/layout/customer/CustomerNotification.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useNotificationStore from "../../store/useNotificationStore";
import { formatTimeAgo } from "../../utils/formatTimeAgo";
import { Loader2, Mail, MailOpen, Inbox, CheckCheck } from "lucide-react";

const CustomerNotification = () => {
  const navigate = useNavigate();
  const {
    notifications,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAsUnread,
    markAllAsRead,
  } = useNotificationStore();
  const [activeTab, setActiveTab] = useState("unread"); // Default to 'unread'

  useEffect(() => {
    if (activeTab === "unread") {
      fetchNotifications(false); // is_read=false for unread
    } else {
      fetchNotifications(null); // is_read=null for all
    }
  }, [activeTab, fetchNotifications]);

  const handleNotificationClick = (notification) => {
    if (notification.is_read === 0) {
      markAsRead(notification.id);
    }
    if (notification.reference_id) {
      navigate(`/customer/project/${notification.reference_id}/detail`);
    }
  };

  const handleMarkAll = async () => {
    await markAllAsRead();
  };

  const renderActionButtons = (notification) => {
    if (activeTab === 'unread') {
      return (
        <button
          onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
          className="text-xs text-blue-500 hover:underline flex items-center gap-1"
          title="Mark as read"
        >
          <MailOpen className="w-4 h-4" /> Mark as Read
        </button>
      );
    }
    // activeTab is 'all'
    if (notification.is_read === 0) {
      return (
        <button
          onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
          className="text-xs text-blue-500 hover:underline flex items-center gap-1"
          title="Mark as read"
        >
          <MailOpen className="w-4 h-4" /> Mark as Read
        </button>
      );
    } else {
      return (
        <button
          onClick={(e) => { e.stopPropagation(); markAsUnread(notification.id); }}
          className="text-xs text-gray-500 hover:underline flex items-center gap-1"
          title="Mark as unread"
        >
          <Mail className="w-4 h-4" /> Mark as Unread
        </button>
      );
    }
  };

  return (
    <div className="p-4 bg-gray-100 shadow-md max-w-full mx-auto" style={{ borderRadius: '12px' }}>
      <div className="flex justify-between items-center mb-4">
        <div className="flex space-x-2">
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "unread" ? "bg-blue-600 text-white shadow-sm" : "bg-white text-gray-600 hover:bg-gray-100"}`}
            onClick={() => setActiveTab("unread")}
          >
            Unread
          </button>
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "all" ? "bg-blue-600 text-white shadow-sm" : "bg-white text-gray-600 hover:bg-gray-100"}`}
            onClick={() => setActiveTab("all")}
          >
            All Notifications
          </button>
        </div>
        <button
          onClick={handleMarkAll}
          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
        >
          <CheckCheck className="w-4 h-4" />
          Mark all as read
        </button>
      </div>
      <div className="bg-white rounded-lg shadow-inner">
        {isLoading ? (
          <div className="flex justify-center items-center h-60">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col justify-center items-center h-60 text-gray-500">
            <Inbox className="w-12 h-12 mb-4 text-gray-400" />
            <p className="font-semibold">No notifications here</p>
            <p className="text-sm">You're all caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 flex items-start gap-4 transition-colors cursor-pointer group ${notification.is_read === 0 ? 'bg-blue-50/50' : 'hover:bg-gray-50'}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex-grow">
                  <div className="flex justify-between items-start">
                    <p className={`font-semibold text-sm ${notification.is_read === 0 ? 'text-gray-800' : 'text-gray-600'}`}>{notification.title}</p>
                    <span className="text-xs text-gray-400 whitespace-nowrap ml-4">{formatTimeAgo(notification.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{notification.message}</p>
                  {/* Action buttons below content on mobile, right on desktop */}
                  <div className="mt-3 flex sm:hidden">
                    {renderActionButtons(notification)}
                  </div>
                </div>
                {/* Desktop: action buttons on the right, hidden on mobile */}
                <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden sm:block">
                  {renderActionButtons(notification)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerNotification;